import ElementLoader from "components/ElementLoader";
import RecordTable from "components/Table/RecordTable";
import { getInitialQueryWithTableSettings } from "components/Table/tableUtils";
import { nameColumn } from "containers/Resources/screens/ResourcesSingle/components/UsedByList/utils";
import { useMemo } from "react";
import { getTypedLowerCase } from "utils/service";
import { useReferencesTo } from "utils/useReferencesTo";
import styles from "./UsedByList.module.scss";
import { buildUsedBySections, prettyRecordType, ReferenceRow } from "./usedBySections";

/**
 * Generic "Used by" (reverse-reference) tab.
 *
 * Calls the `POST /references-to` endpoint (backend `reference_resolver`) to list
 * every record that references the viewed record -- the inbound / reverse of a
 * normal reference lookup.
 *
 * Presentation (per Margot's review of the reverse-reference tab): the endpoint
 * returns one group per (referencing type, column/attribute), but rendering each
 * as its own stacked mini-table read as "several different lists on top of each
 * other". Instead:
 *   - all FK / custom-object REFERENCE groups are flattened into ONE standard
 *     record list (Name + Type + "Referenced via" columns), styled like any other
 *     resource list, so a mixed set of referrers reads as a single list; and
 *   - RUNTIME usage (the precomputed `resource_usage` group: flows that use this
 *     connector / wrapper / sub-flow when they run) is kept as a SEPARATE,
 *     clearly-labelled section, because "a flow ran this at runtime" is a
 *     different relationship from "a record points at this".
 *
 * This is the unified reverse-reference core: it is registered on record types
 * that have no bespoke used-by tab today (connector, object_template, ...). The
 * curated per-relationship tabs (custom_object "Used by", flow Schedules/Webhooks,
 * setting lock tabs -- see {@link UsedBySingleRecordList}) are migrated onto this
 * same component in a later phase.
 */

interface ReferencesUsedByListProps {
    resourceName: string;
    id: string;
}

const initialQuery = { limit: 25, order: "+name", offset: 0 };

const tableOptions = {
    // "controls" = the right-side display controls (column selector) without the
    // left bulk-action chrome; parity with the app's other reverse-usage list
    // (UsedBySingleRecordList). selectableRows "none" -> no stray bulk actions.
    toolbar: "controls" as const,
    toolbarFilters: false,
    showSearch: false,
    viewColumns: true,
    showDensityToggle: false,
    selectableRows: "none" as const,
};

/** Plain-text "Type" column -- the referring record's type. */
const referencingTypeColumn = {
    label: "Type",
    key: "referencing_type_label",
    shown: true,
    sortable: true,
    dto: (data: ReferenceRow) => data?.referencing_type_label ?? null,
};

/** Plain-text "Referenced via" column -- the column / attribute holding the reference. */
const viaColumn = {
    label: "Referenced via",
    key: "via_label",
    shown: true,
    sortable: true,
    dto: (data: ReferenceRow) => data?.via_label ?? null,
};

const referenceColumns = [nameColumn, referencingTypeColumn, viaColumn];

export const ReferencesUsedByList = ({ resourceName, id }: ReferencesUsedByListProps) => {
    const { groups, isLoading, error, refetch } = useReferencesTo(resourceName, id);

    // Split into the mixed reference list + the separate runtime-usage
    // section(s) (see buildUsedBySections).
    const { referenceRows, referencesTruncated, runtimeGroups } = useMemo(
        () => buildUsedBySections(groups),
        [groups]
    );

    if (isLoading && groups === null) {
        return (
            <div className={styles.referencesLoading}>
                <ElementLoader />
            </div>
        );
    }

    if (error) {
        return <div className={styles.referencesEmpty}>{error}</div>;
    }

    if (referenceRows.length === 0 && runtimeGroups.length === 0) {
        return (
            <div className={styles.referencesEmpty}>
                No other records reference this record.
            </div>
        );
    }

    // A section header clarifies the two-section case; a lone references list
    // stands on its own (reads as a plain resource list, no redundant header).
    const showSectionHeaders = referenceRows.length > 0 && runtimeGroups.length > 0;

    return (
        <div className={styles.usedByFormContainer}>
            {referenceRows.length > 0 ? (
                <div className={styles.referenceGroup}>
                    {showSectionHeaders ? (
                        <div className={styles.referenceGroupHeader}>
                            <span className={styles.referenceGroupTitle}>References</span>
                            <span className={styles.referenceGroupVia}>
                                records that reference this one
                            </span>
                        </div>
                    ) : null}
                    <RecordTable
                        columns={referenceColumns}
                        data={referenceRows}
                        totalItemCount={referenceRows.length}
                        isLoading={false}
                        recordTypes="all"
                        initialQuery={getInitialQueryWithTableSettings(
                            `referencesUsedBy-${resourceName}`,
                            initialQuery
                        )}
                        tableSettingsKey={`referencesUsedBy-${resourceName}`}
                        options={{
                            ...tableOptions,
                            pagination: referenceRows.length > initialQuery.limit,
                        }}
                        refresh={refetch}
                    />
                    {referencesTruncated ? (
                        <div className={styles.referenceGroupTruncated}>
                            Showing a sample — more references exist.
                        </div>
                    ) : null}
                </div>
            ) : null}

            {runtimeGroups.map((group) => {
                const feType = getTypedLowerCase(group.referencing_record_type);
                const rows = group.items.map((item) => ({
                    id: item.id,
                    name: item.name ?? undefined,
                    record_type: feType,
                }));
                return (
                    <div
                        key={`runtime:${group.referencing_record_type}`}
                        className={styles.referenceGroup}
                    >
                        <div className={styles.referenceGroupHeader}>
                            <span className={styles.referenceGroupTitle}>Used at runtime</span>
                            <span className={styles.referenceGroupVia}>
                                {prettyRecordType(group.referencing_record_type)}s that use
                                this record when they run
                            </span>
                        </div>
                        <RecordTable
                            columns={[nameColumn]}
                            data={rows}
                            totalItemCount={rows.length}
                            isLoading={false}
                            recordTypes="all"
                            initialQuery={initialQuery}
                            options={{
                                ...tableOptions,
                                viewColumns: false,
                                pagination: rows.length > initialQuery.limit,
                            }}
                            refresh={refetch}
                        />
                        {group.truncated ? (
                            <div className={styles.referenceGroupTruncated}>
                                Showing the first {group.items.length} — more exist.
                            </div>
                        ) : null}
                    </div>
                );
            })}
        </div>
    );
};
